import { getPublicPlanningOptions } from '@gozoki/api'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { DateTime } from 'luxon'
import z from 'zod'
import logo from '~/assets/logos/logo.webp'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { Title } from '~/components/new/app-ui/title'
import { PublicPlanningCalendar } from '~/components/planning-share/PublicPlanningCalendar'

export const Route = createFileRoute('/_nosidebar/planning')({
  validateSearch: z.object({
    token: z
      .string()
      .regex(/^[a-f0-9]{64}$/)
      .optional(),
  }),
  component: PublicPlanningPage,
})

const PageShell = ({ children }: { children: React.ReactNode }) => (
  <div className="flex h-screen flex-col gap-2 overflow-hidden p-2 sm:p-4">{children}</div>
)

const PublicHeader = ({
  title,
  subtitle,
  storeLabel,
  storeAddress,
}: {
  title: string
  subtitle?: string
  storeLabel?: string
  storeAddress?: string
}) => (
  <header className="flex flex-col gap-4 rounded-2xl border border-sidebar-border bg-sidebar p-4 sm:flex-row sm:items-center">
    <div className="flex items-center gap-4">
      <img src={logo} alt="Lucie Ultrafrais" className="h-10 w-auto shrink-0 sm:h-14" />
      <Title subtitle={subtitle} className="min-w-0 flex-1 [&_h1]:text-2xl sm:[&_h1]:text-4xl">
        {title}
      </Title>
    </div>
    {storeLabel && (
      <div className="flex flex-col rounded-xl border border-sidebar-border bg-background px-4 py-2 sm:ml-auto sm:text-right">
        <span className="text-lg font-semibold text-primary sm:text-xl">{storeLabel}</span>
        {storeAddress && <span className="text-sm text-muted-foreground">{storeAddress}</span>}
      </div>
    )}
  </header>
)

const InvalidLink = () => (
  <PageShell>
    <PublicHeader title="Lien invalide ou expiré" />
    <AppContainer>
      <p className="text-muted-foreground">
        Merci de contacter votre manager pour obtenir un nouveau lien.
      </p>
    </AppContainer>
  </PageShell>
)

function PublicPlanningPage() {
  const { token } = Route.useSearch()

  if (!token) return <InvalidLink />

  return <PublicPlanningPageWithToken token={token} />
}

function PublicPlanningPageWithToken({ token }: { token: string }) {
  const { data, isLoading, isError } = useQuery(getPublicPlanningOptions(token))

  if (isLoading) {
    return (
      <PageShell>
        <PublicHeader title="Chargement..." />
        <AppContainer>
          <p className="text-muted-foreground">Chargement du planning...</p>
        </AppContainer>
      </PageShell>
    )
  }

  if (isError || !data) return <InvalidLink />

  const firstName = data.collaborator.firstName ?? ''
  const weekStart = DateTime.fromISO(data.period.weekStart).setLocale('fr')
  const weekEnd = DateTime.fromISO(data.period.weekEnd).setLocale('fr')
  const periodLabel = `Semaine du ${weekStart.toLocaleString({ day: 'numeric', month: 'long' })} au ${weekEnd.toLocaleString({ day: 'numeric', month: 'long', year: 'numeric' })}`
  const storeAddress = data.store.address
    ? `${data.store.address.street}, ${data.store.address.postalCode} ${data.store.address.city}`
    : undefined

  return (
    <PageShell>
      <PublicHeader
        title={`Bonjour ${firstName || 'collaborateur'}`}
        subtitle={periodLabel}
        storeLabel={data.store.label}
        storeAddress={storeAddress}
      />
      <AppContainer className="min-h-0 overflow-x-auto">
        <div className="flex min-h-0 min-w-[900px] flex-1 flex-col">
          <PublicPlanningCalendar
            collaborators={data.collaborators}
            shifts={data.shifts}
            weekStart={data.period.weekStart}
            highlightCollaboratorId={data.collaborator.id}
          />
        </div>
      </AppContainer>
    </PageShell>
  )
}
